import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreditCardFormProps {
  onSuccess: () => void;
}

export const CreditCardForm: React.FC<CreditCardFormProps> = ({ onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    limite_credito: "",
    dia_vencimento: "",
    dia_fechamento: "",
    bandeira: "",
    cor: "#3B82F6",
    instituicao_nome: "",
  });



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.limite_credito || !formData.dia_vencimento || !formData.dia_fechamento) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    const diaVencimento = parseInt(formData.dia_vencimento);
    const diaFechamento = parseInt(formData.dia_fechamento);
    
    if (diaVencimento < 1 || diaVencimento > 31) {
      toast.error("O dia de vencimento deve estar entre 1 e 31.");
      return;
    }
    
    if (diaFechamento < 1 || diaFechamento > 31) {
      toast.error("O dia de fechamento deve estar entre 1 e 31.");
      return;
    }

    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Você precisa estar logado para criar um cartão.");
        return;
      }

      const { error } = await supabase.from("contas").insert({
        nome: formData.nome,
        tipo: "cartao_credito",
        cor: formData.cor,
        saldo_inicial: 0,
        saldo_atual: 0,
        ativo: true,
        limite_credito: parseFloat(formData.limite_credito),
        dia_vencimento: diaVencimento,
        dia_fechamento: diaFechamento,
        bandeira: formData.bandeira || null,
        fatura_atual: 0,
        instituicao_nome: formData.instituicao_nome || null,
        user_id: user.id,
      });

      if (error) {
        console.error("Erro ao criar cartão:", error);
        toast.error("Erro ao criar cartão de crédito.");
        return;
      }

      toast.success("Cartão de crédito criado com sucesso!");

      setFormData({
        nome: "",
        limite_credito: "",
        dia_vencimento: "",
        dia_fechamento: "",
        bandeira: "",
        cor: "#3B82F6",
        instituicao_nome: "",
      });
      setIsOpen(false);
      onSuccess();
    } catch (error) {
      console.error("Erro inesperado:", error);
      toast.error("Erro inesperado ao criar cartão.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <CreditCard className="h-4 w-4" />
          Novo Cartão
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Novo Cartão de Crédito
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome do Cartão *</Label>
            <Input
              id="nome"
              type="text"
              placeholder="Ex: Cartão Nubank"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instituicao_nome">Banco/Instituição</Label>
            <Input
              id="instituicao_nome"
              type="text"
              placeholder="Ex: Nubank, Itaú, Bradesco"
              value={formData.instituicao_nome}
              onChange={(e) => setFormData({ ...formData, instituicao_nome: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="limite_credito">Limite de Crédito *</Label>
            <CurrencyInput
              id="limite_credito"
              placeholder="0,00"
              value={formData.limite_credito}
              onChange={(value) => setFormData({ ...formData, limite_credito: value.toString() })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bandeira">Bandeira do Cartão</Label>
            <Select value={formData.bandeira} onValueChange={(value) => setFormData({ ...formData, bandeira: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a bandeira" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Visa">Visa</SelectItem>
                <SelectItem value="Mastercard">Mastercard</SelectItem>
                <SelectItem value="Elo">Elo</SelectItem>
                <SelectItem value="American Express">American Express</SelectItem>
                <SelectItem value="Hipercard">Hipercard</SelectItem>
                <SelectItem value="Diners Club">Diners Club</SelectItem>
                <SelectItem value="Discover">Discover</SelectItem>
                <SelectItem value="Outros">Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dia_fechamento">Dia Fechamento *</Label>
              <Select value={formData.dia_fechamento} onValueChange={(value) => setFormData({ ...formData, dia_fechamento: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Dia" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dia_vencimento">Dia Vencimento *</Label>
              <Select value={formData.dia_vencimento} onValueChange={(value) => setFormData({ ...formData, dia_vencimento: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Dia" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cor">Cor</Label>
            <Input
              id="cor"
              type="color"
              value={formData.cor}
              onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
              className="h-10 w-full"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? "Criando..." : "Criar Cartão"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};